import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statusesApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import ConfirmModal from '../components/ConfirmModal';
import './StatusAdmin.css';

function StatusAdmin() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const DEFAULT_COLOR = '#6c757d';
  const [formData, setFormData] = useState({ status: '', color: DEFAULT_COLOR });
  const [saving, setSaving] = useState(false);

  const loadStatuses = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await statusesApi.getAll();
      const data = response.data?.data ?? response.data;
      setStatuses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading statuses:', err);
      setError(err.response?.data?.message || 'Failed to load statuses');
      setStatuses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.profile !== 'admin') {
      navigate('/profile');
      return;
    }
    if (user?.profile === 'admin') loadStatuses();
  }, [user, authLoading, navigate, loadStatuses]);

  const handleCreate = () => {
    setEditingStatus(null);
    setFormData({ status: '', color: DEFAULT_COLOR });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (row) => {
    setEditingStatus(row);
    setFormData({
      status: row.status || '',
      color: row.color && row.color.trim() ? row.color.trim() : DEFAULT_COLOR,
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingStatus(null);
    setFormData({ status: '', color: DEFAULT_COLOR });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const status = formData.status.trim();
    if (!status) {
      setError('Status is required');
      return;
    }
    setSaving(true);
    setError('');
    const payload = { status };
    if (formData.color && formData.color.trim()) payload.color = formData.color.trim();
    try {
      if (editingStatus) {
        await statusesApi.update(editingStatus.id, payload);
        setSuccess('Status updated');
      } else {
        await statusesApi.create(payload);
        setSuccess('Status created');
      }
      await loadStatuses();
      setShowForm(false);
      setEditingStatus(null);
      setFormData({ status: '', color: DEFAULT_COLOR });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save status');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (row) => {
    setStatusToDelete(row);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!statusToDelete) return;
    setError('');
    try {
      await statusesApi.delete(statusToDelete.id);
      setSuccess('Status deleted');
      setShowDeleteModal(false);
      setStatusToDelete(null);
      await loadStatuses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete status');
      setShowDeleteModal(false);
      setStatusToDelete(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  if (authLoading) return <Loading />;
  if (user?.profile !== 'admin') return null;

  return (
    <>
      <Header />
      <div className="container">
        <div className="status-admin-page">
          <div className="admin-header">
            <div className="admin-header-top">
              <Link to="/admin/rules" className="btn btn-secondary back-btn">
                <i className="bi bi-arrow-left"></i> Back to Rules Admin
              </Link>
              <h1>Status Admin (gr_status)</h1>
            </div>
            {!showForm && (
              <div className="admin-header-buttons">
                <button onClick={handleCreate} className="btn btn-primary">
                  <i className="bi bi-plus-circle"></i> Create New Status
                </button>
              </div>
            )}
          </div>

          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {showForm ? (
            <form onSubmit={handleSubmit} className="status-admin-form">
              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <input
                  type="text"
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  disabled={saving}
                  maxLength={50}
                  placeholder="e.g. submit, vote1, vote2"
                />
              </div>
              <div className="form-group button-color-group">
                <label htmlFor="status-color">Button color</label>
                <div className="color-and-preview-row">
                  <div className="color-picker-left">
                    <div className="color-picker-swatch-wrap">
                      <input
                        type="color"
                        id="status-color"
                        name="color"
                        value={(() => {
                          const c = (formData.color || '').trim() || DEFAULT_COLOR;
                          return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : DEFAULT_COLOR;
                        })()}
                        onChange={handleInputChange}
                        disabled={saving}
                        className="color-picker-input"
                        title="Pick button color"
                      />
                      <span
                        className="color-picker-swatch"
                        style={{
                          backgroundColor: (() => {
                            const c = (formData.color || '').trim() || DEFAULT_COLOR;
                            return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : DEFAULT_COLOR;
                          })(),
                        }}
                        aria-hidden
                      />
                    </div>
                    <input
                      type="text"
                      className="color-hex-input"
                      value={formData.color ?? ''}
                      onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
                      placeholder={DEFAULT_COLOR}
                      maxLength={20}
                      disabled={saving}
                      spellCheck={false}
                      aria-label="Hex color code"
                    />
                  </div>
                  <div className="button-preview-wrap">
                    <span className="preview-label">Preview</span>
                    <button
                      type="button"
                      className="status-button-preview"
                      style={{
                        backgroundColor: (() => {
                          const c = (formData.color || '').trim() || DEFAULT_COLOR;
                          return /^#[0-9A-Fa-f]{6}$/.test(c) ? c : DEFAULT_COLOR;
                        })(),
                      }}
                      disabled
                      aria-hidden
                    >
                      {formData.status?.trim() || 'status'}
                    </button>
                  </div>
                </div>
                <small className="form-hint">Used for the status filter buttons on the Submission Workflow page.</small>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingStatus ? 'Update Status' : 'Create Status')}
                </button>
                <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <>
              {loading ? (
                <div className="loading">Loading statuses...</div>
              ) : statuses.length === 0 ? (
                <p className="no-items">No statuses found. Create one to get started.</p>
              ) : (
                <div className="status-admin-list">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Color</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statuses.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.status}</td>
                          <td>
                            {row.color ? (
                              <span className="color-swatch-wrap">
                                <span
                                  className="color-swatch"
                                  style={{ backgroundColor: row.color }}
                                  title={row.color}
                                />
                                <span className="color-value">{row.color}</span>
                              </span>
                            ) : (
                              <span className="no-color">—</span>
                            )}
                          </td>
                          <td className="actions-cell">
                            <button
                              type="button"
                              className="btn btn-sm btn-edit"
                              onClick={() => handleEdit(row)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-delete"
                              onClick={() => handleDeleteClick(row)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i> Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        show={showDeleteModal}
        title="Delete Status"
        message={
          statusToDelete
            ? `Are you sure you want to delete the status "${statusToDelete.status}"? This may affect submission workflow if this status is in use.`
            : ''
        }
        confirmText="Delete"
        confirmType="danger"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteModal(false);
          setStatusToDelete(null);
        }}
      />

      <Footer />
    </>
  );
}

export default StatusAdmin;
